import { Grid, IconButton, Tooltip } from "@mui/material";
import SuiBox from "components/SuiBox";
import SuiInput from "components/SuiInput";
import Alert from "custom/Alert";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import DashboardNavbar from "custom/Navbars/DashboardNavbar";
import DataTable from "custom/Tables/DataTable";
import MessageTypes from "enums/message-types";
import { FileArrowDown, FunnelSimple, Pencil, TrashSimple } from "phosphor-react";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { removeMessage } from "redux/actions/message";
import { getSubOrganizationListByOrganizationId } from "redux/actions/pages/sub-organization";

import { startAllOrganizationsScreen } from "redux/actions/pages/organization";

import Swal from "sweetalert2";
import SuiTypography from "components/SuiTypography";
import CustomFormField from "components/CustomFormField";
import { Formik } from "formik";
import { ALL_USERS, startAllUsersScreen, softDeleteUSer } from "redux/actions/pages/user";
import { startAllClassesScreen } from "redux/actions/pages/class";

import ExcelJS from "exceljs";
import SuiButton from "components/SuiButton";

const UserList = ({
  message,
  localizations,
  allSubOrganizations,
  startScreen,
  removeErrorMsg,
  language,
  allOrganizations,
  listByOrganizationId,
  getAllOrganizations,
  userList,
  fetchClasses,
  classes,
  softDelete,
}) => {
  const [userFilter, setUserFilter] = useState("");
  const history = useHistory();

  const [selectedFilters, setSelectedFilters] = useState({
    organizationId: null,
    subOrganizationId: null,
  });

  const getClassNames = (classIdString) => {
    if (!classIdString || !classes || classes.length === 0) return "-";

    const ids = classIdString
      .toString()
      .split(",")
      .map((id) => id.trim());

    const foundNames = classes.filter((c) => ids.includes(c.id.toString())).map((c) => c.className);
    return foundNames.length > 0 ? foundNames.join(", ") : "-";
  };

  useEffect(() => {
    startScreen();
    if (!allOrganizations || allOrganizations.length === 0) {
      getAllOrganizations();
    }
  }, []);

  useEffect(() => {
    if (
      message &&
      message.messageContent !== undefined &&
      message.messageContent !== null &&
      message.messageContent !== ""
    ) {
      Swal.fire({
        icon: message.messageType || "error",
        title:
          message.messageType === MessageTypes.success
            ? localizations?.MESSAGE_SUCCESS ?? "BAŞARILI!"
            : localizations?.MESSAGE_ERROR ?? "HATA!",
        text: message.messageContent,
        showCancelButton: false,
        confirmButtonText: localizations?.MESSAGE_GO_HOME_CONFIRM_BTN,
      }).then((result) => {
        removeErrorMsg(ALL_USERS);
        if (result.isConfirmed) {
          history.push("/");
        }
      });
    }
    // eslint-disable-next-line
  }, [message]);

  const columns = [
    { Header: localizations?.USER ?? "User", accessor: "user" },
    { Header: localizations?.EMAIL ?? "Email", accessor: "email" },
    { Header: localizations?.PASSWORD ?? "Password", accessor: "password" },
    { Header: localizations?.ROLE ?? "Role", accessor: "role" },
    { Header: localizations?.GRADE ?? "Grade", accessor: "classInfo" },
    {
      Header: localizations?.ALL_BOOKS_ENABLED ?? "All books enabled",
      accessor: "allBooksEnabled",
    },
    {
      Header: localizations?.ACTIONS ?? "Actions",
      accessor: "preview",
      // eslint-disable-next-line react/prop-types
      Cell: ({ value: [data] }) => (
        <SuiBox mx={1} display="flex">
          <Tooltip title={localizations?.EDIT ?? "Düzenle"} placement="bottom">
            <IconButton
              aria-label={localizations?.EDIT}
              onClick={(e) => {
                e.preventDefault();
                history.push({
                  pathname: "/user/edit",
                  state: { userId: data.id },
                });
              }}
            >
              <Pencil size={32} weight="duotone" color="#7493b6" />
            </IconButton>
          </Tooltip>
          <Tooltip title={localizations?.DELETE ?? "Sil"} placement="bottom">
            <IconButton
              aria-label={localizations?.DELETE}
              onClick={(e) => {
                e.preventDefault();
                softDelete(data.id);
                startScreen({
                  organizationId: selectedFilters.organizationId,
                  subOrganizationId: selectedFilters.subOrganizationId,
                });
              }}
            >
              <TrashSimple size={32} weight="duotone" color="#ff0000" />
            </IconButton>
          </Tooltip>
        </SuiBox>
      ),
    },
  ];

  const getDataTableData = (dataList, filter, lang) => ({
    columns,
    rows:
      dataList && dataList.length > 0
        ? dataList
            .filter((user) => {
              let lowercaseFilter = "";
              if (filter && filter !== "") {
                lowercaseFilter = filter.toLocaleLowerCase(lang);
              }
              return (
                user.name?.toLocaleLowerCase(lang).includes(lowercaseFilter) ||
                user.surname?.toLocaleLowerCase(lang).includes(lowercaseFilter) ||
                getClassNames(user.classIdList).toLocaleLowerCase(lang).includes(lowercaseFilter) ||
                user.userType?.toLocaleLowerCase(lang).includes(lowercaseFilter) ||
                user.email?.toLocaleLowerCase(lang).includes(lowercaseFilter)
              );
            })
            .map((user) => ({
              user: `${user.name || ""} ${user.surname || ""}`,
              email: user.email,
              password: user.generatedPassword,
              role: user.userType,
              classInfo: getClassNames(user.classIdList),
              allBooksEnabled: user.allBooksEnabled
                ? localizations?.YES ?? "Evet"
                : localizations?.NO ?? "Hayır",
              preview: [{ id: user.id }],
            }))
        : [],
  });

  const memoizedTableData = useMemo(
    () => getDataTableData(userList, userFilter, language),
    [userFilter, userList, classes, language]
  );

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Kullanıcı Listesi");
    worksheet.columns = [
      { header: "Kullanıcı Adı", key: "user", width: 25 },
      { header: "Email", key: "email", width: 30 },
      { header: "Şifre", key: "password", width: 15 },
      { header: "Rol", key: "role", width: 15 },
      { header: "Sınıf", key: "classInfo", width: 30 },
      { header: "Tüm Kitaplara Erişim", key: "allBooksEnabled", width: 20 },
    ];

    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF4CAF50" },
    };

    memoizedTableData.rows.forEach((user) => {
      worksheet.addRow({
        user: user.user,
        email: user.email,
        password: user.password,
        role: user.role === "OTHER" ? "ÖĞRETMEN" : "ÖĞRENCİ",
        classInfo: user.classInfo,
        allBooksEnabled: user.allBooksEnabled,
      });
    });

    const organization = selectedFilters.organizationId
      ? allOrganizations.find((org) => org.id === selectedFilters.organizationId)?.organizationKey
      : "";
    const subOrganization = selectedFilters.subOrganizationId
      ? allSubOrganizations.find((subOrg) => subOrg.id === selectedFilters.subOrganizationId)
          ?.subOrganizationKey
      : "";

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `kullanicilar_${organization}_${subOrganization}_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;
    anchor.click();
    window.URL.revokeObjectURL(url);
  };
  return (
    <>
      <Alert />
      <DashboardLayout>
        <DashboardNavbar />
        <SuiBox py={3}>
          <Grid container>
            <Grid item xs={12} sm={7}>
              <h3>{localizations?.ALL_USERS ?? "Kullanıcı Listesi"}</h3>
            </Grid>
            <Grid
              item
              sm={5}
              xs={12}
              sx={{
                textAlign: "right",
                display: "flex",
                flexDirection: { sm: "row", xs: "column" },
                gap: 2,
                alignItems: { sm: "center", xs: "flex-start" },
                justifyContent: { sm: "flex-end", xs: "flex-start" },
              }}
            >
              <Grid item lg={10} xs={12} sm={8} sx={{ width: { sm: "100%", xs: "100%" } }}>
                <SuiInput
                  size="small"
                  type="search"
                  placeholder={localizations?.USER_FILTER_INPUT_PLACEHOLDER ?? "Kullanıcı Ara..."}
                  icon={{ component: <FunnelSimple />, direction: "left" }}
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                />
              </Grid>
              <Grid item lg={4} xs={12} sm={8}>
                <SuiButton
                  size="small"
                  type="button"
                  color="success"
                  onClick={exportToExcel}
                  sx={{
                    backgroundColor: "#4caf50",
                    color: "white",
                    "&:hover": { backgroundColor: "#45a049" },
                  }}
                  disabled={userList?.length === 0}
                >
                  {localizations?.EXPORT_TO_EXCEL ?? "Excel'e Aktar"}
                  <FileArrowDown size={24} weight="duotone" />
                </SuiButton>
              </Grid>
            </Grid>
            <Grid item lg={12} xs={12} mt={3}>
              <SuiTypography variant="h6" fontWeight="medium">
                {localizations?.ALL_USERS ?? "User Listesi"}
              </SuiTypography>

              <Formik
                initialValues={{
                  organization: null,
                  subOrganization: null,
                }}
                onSubmit={() => {}}
              >
                {(formikProps) => {
                  const { setFieldValue, values, errors, touched, setFieldTouched } = formikProps;
                  return (
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <CustomFormField
                          options={
                            allOrganizations &&
                            allOrganizations?.map((organization) => ({
                              value: organization.id,
                              label: organization.organizationKey,
                            }))
                          }
                          type="select"
                          placeholder="Kurum Seçiniz..."
                          value={values.organization}
                          setFieldValue={(field, value) => {
                            setFieldValue(field, value);
                            setFieldValue("subOrganization", null);
                            setSelectedFilters({
                              organizationId: value?.value ? value.value : null,
                              subOrganizationId: null,
                            });
                            startScreen({});
                            if (value && value.value) {
                              listByOrganizationId({ organizationId: value.value });
                            }
                          }}
                          label={localizations?.ORGANIZATION ?? "ORGANIZATION"}
                          name="organization"
                          touched={touched}
                          errors={errors}
                          error={false}
                          success={false}
                          setFieldTouched={setFieldTouched}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <CustomFormField
                          disabled={!values.organization}
                          options={
                            allSubOrganizations &&
                            allSubOrganizations.map((subOrg) => ({
                              value: subOrg.id,
                              label: subOrg.subOrganizationKey,
                            }))
                          }
                          type="select"
                          placeholder="Şube Seçiniz..."
                          value={values.subOrganization}
                          label={localizations?.SUB_ORGANIZATION ?? "SUB ORGANIZATION"}
                          name="subOrganization"
                          touched={touched}
                          errors={errors}
                          setFieldTouched={setFieldTouched}
                          setFieldValue={(field, value) => {
                            setFieldValue(field, value);

                            if (
                              values.organization &&
                              values.organization.value &&
                              value &&
                              value.value
                            ) {
                              const orgId = values.organization.value;
                              const subOrgId = value.value;

                              setSelectedFilters({
                                organizationId: orgId,
                                subOrganizationId: subOrgId,
                              });

                              startScreen({
                                organizationId: orgId,
                                subOrganizationId: subOrgId,
                              });

                              fetchClasses({ organizationId: orgId, subOrganizationId: subOrgId });
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  );
                }}
              </Formik>
            </Grid>
            <Grid item lg={12} xs={12} mt={3}>
              <DataTable
                table={memoizedTableData}
                entriesPerPage={{ defaultValue: 5, entries: [5, 10, 25, 50] }}
                pagination={{ variant: "contained", color: "dark" }}
              />
            </Grid>
          </Grid>
        </SuiBox>
      </DashboardLayout>
    </>
  );
};

UserList.defaultProps = {
  allSubOrganizations: [],
  message: null,
  localizations: null,
  language: "TR",
  listByOrganizationId: () => {},
  allOrganizations: [],
  getAllOrganizations: () => {},
  userList: [],
  fetchClasses: () => {},
  classes: [],
  softDelete: () => {},
};

UserList.propTypes = {
  allSubOrganizations: PropTypes.arrayOf(PropTypes.any),
  message: PropTypes.objectOf(PropTypes.any),
  localizations: PropTypes.objectOf(PropTypes.any),
  startScreen: PropTypes.func.isRequired,
  removeErrorMsg: PropTypes.func.isRequired,
  language: PropTypes.string,
  listByOrganizationId: PropTypes.func,
  allOrganizations: PropTypes.arrayOf(PropTypes.any),
  getAllOrganizations: PropTypes.func,
  userList: PropTypes.arrayOf(PropTypes.any),
  fetchClasses: PropTypes.func,
  classes: PropTypes.arrayOf(PropTypes.any),
  softDelete: PropTypes.func,
};

const mapStateToProps = (state) => {
  const subOrganizations = Array.isArray(state?.data?.subOrganizations)
    ? state.data.subOrganizations
    : [];

  const organizations = Array.isArray(state?.data?.organizations) ? state.data.organizations : [];
  const users = Array.isArray(state?.data?.userData) ? state.data.userData : [];
  const classList = Array.isArray(state?.data?.classes) ? state.data.classes : [];
  return {
    allSubOrganizations: subOrganizations,
    allOrganizations: organizations,
    message: state.message[ALL_USERS],
    localizations: state.localization.pairs,
    language: state.localization.language ?? "TR",
    userList: users,
    classes: classList,
  };
};

const mapDispatchToProps = {
  startScreen: startAllUsersScreen,
  removeErrorMsg: removeMessage,
  listByOrganizationId: getSubOrganizationListByOrganizationId,
  getAllOrganizations: startAllOrganizationsScreen,
  fetchClasses: startAllClassesScreen,
  softDelete: softDeleteUSer,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserList);
